/*
 * The Foozie: collection pages (session A5a, 2026-09-24).
 *
 * Four small custom elements, no framework, loaded as one module after the
 * page is interactive:
 *   <fz-grid-filter>       the size chips above a grid
 *   <fz-collection-tools>  live search, type and colour above a grid (2026-10-09)
 *   <fz-grid-add>          an inline ADD TO CART on a card
 *   <fz-bundle-counter>    the bundle page's sticky "{n} of 4 added" bar
 * plus a delegated opener for the size-guide <dialog>.
 *
 * THE CART CONTRACT IS HORIZON'S, so the drawer (session A4) needs nothing
 * from this file. An add POSTs Theme.routes.cart_add_url with the same
 * `sections` parameter Horizon's product form sends, dispatches the standard
 * CartLinesUpdateEvent from @shopify/events and resolves its promise with the
 * cart and the re-rendered sections. The drawer, the header count and the
 * live region all listen for that one event. A4 agreed on 2026-09-24 to skip
 * its auto-open when the event's context is 'fz-bundle', so four adds on the
 * bundle page do not open the drawer four times.
 *
 * INP BUDGET: 200 ms at 4x CPU slowdown (plan check P). Every handler does
 * its visible work synchronously and cheaply (a class, an attribute, a
 * textContent) and leaves the network to the promise, so the next paint is
 * never waiting on anything but its own DOM writes.
 */

import { CartLinesUpdateEvent, StandardEvents } from "@shopify/events";
// Defines <collection-component>, Horizon's collection-viewed event wrapper,
// which sections/fz-collection keeps (Horizon loads it only with its cards).
import "@theme/view-event-elements";

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* A1's --fz-spring-soft (0.45 s, bounce 0.15), read once for WAAPI, which
   cannot take a custom property. Falls back to the strong ease-out. */
const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";
const SPRING = (() => {
  // IA-13: iOS 16.4-17.1 has no linear() easing, and animate() THROWS on an
  // easing it cannot parse. Take the token only where the engine accepts it.
  const v = getComputedStyle(document.documentElement).getPropertyValue("--fz-spring-soft").trim();
  try {
    return v && window.CSS && CSS.supports("transition-timing-function", v) ? v : EASE_OUT;
  } catch {
    return EASE_OUT;
  }
})();

/* A decorative animation never gets to break the render that asked for it. */
function play(element, keyframes, options) {
  try {
    element.getAnimations().forEach((a) => a.cancel());
    element.animate(keyframes, options);
  } catch {
    try {
      element.animate(keyframes, { ...options, easing: EASE_OUT });
    } catch {
      /* no animation, same result */
    }
  }
}

/* ------------------------------------------------------------ pairs */

/*
 * A PAIR is a line the "Bundle - FREE 4th Pair" discount counts, by the one
 * rule the drawer, the cards and the sticky bar share (snippets/fz-pair): its
 * OWN product carries the tag `bundle-4th-free` OR is on the discount's own
 * list, and it is never a native bundle. The counter block renders what the
 * page already knows: the list (data-pair-handles) and, for every product it
 * could see, whether it is a pair (data-pair-ids) and that it was asked
 * (data-known-ids). Gift cards never count.
 *
 * A NATIVE BUNDLE IS NEVER A PAIR. Luke excluded the bundles from Buy 3 Get
 * 4th (2026-10-08). /cart.js says a line is one with `has_components`, a
 * field every line carries (false on the B4 capture of 2026-09-24, before any
 * bundle existed), so the bundle is refused before its product is even
 * looked at; its `Bundle` tag refuses it again if it is ever looked up.
 *
 * /cart.js has no tags, so a product the page never saw (a pre-order pair
 * added from the drawer, say) and not on the list is looked up once from
 * /products/<handle>.js and remembered. A lookup that fails counts the line as
 * no pair: the bar under-counts rather than promise a free pair.
 */
export function pairRule(dataset = {}) {
  return {
    handles: dataset.pairHandles || "",
    pairIds: new Set((dataset.pairIds || "").split("|").filter(Boolean)),
    knownIds: new Set((dataset.knownIds || "").split("|").filter(Boolean)),
  };
}

const onList = (item, rule) => Boolean(item.handle) && rule.handles.includes(`|${item.handle}|`);

export function isPairLine(item, rule = pairRule()) {
  if (!item || item.gift_card || item.has_components) return false;
  return onList(item, rule) || rule.pairIds.has(String(item.product_id));
}

export async function learnTags(cart, rule, fetchProduct = fetchProductTags) {
  const unknown = ((cart && cart.items) || []).filter(
    (item) =>
      !item.gift_card && !item.has_components && !onList(item, rule) && !rule.knownIds.has(String(item.product_id)),
  );
  await Promise.all(
    unknown.map(async (item) => {
      const id = String(item.product_id);
      try {
        const tags = await fetchProduct(item.handle);
        if (tags.includes("bundle-4th-free") && !tags.includes("Bundle")) rule.pairIds.add(id);
        rule.knownIds.add(id);
      } catch {
        /* unknown stays unknown: not counted, and asked again next time */
      }
    }),
  );
}

async function fetchProductTags(handle) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`product ${response.status}`);
  const product = await response.json();
  return Array.isArray(product.tags) ? product.tags : String(product.tags || "").split(/,\s*/);
}

export function pairsIn(cart, rule = pairRule()) {
  let pairs = 0;
  let cents = 0;
  for (const item of (cart && cart.items) || []) {
    if (!isPairLine(item, rule)) continue;
    pairs += item.quantity;
    cents += item.final_line_price ?? item.line_price ?? 0;
  }
  // The discount is the truth about the fourth pair: an automatic discount
  // shows in cart.js as a total_discount and as line discount allocations.
  const items = (cart && cart.items) || [];
  const discounted =
    Boolean(cart && cart.total_discount > 0) ||
    items.some((item) => (item.line_level_discount_allocations || []).length > 0);
  return { pairs, cents, discounted };
}

function money(cents, currency) {
  if (!currency) return "";
  const country = (window.Shopify && window.Shopify.country) || "";
  const lang = document.documentElement.lang || "en";
  const locale = country ? `${lang}-${country}` : lang;
  const whole = cents % 100 === 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(whole ? 0 : 2);
  }
}

async function fetchCart() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const response = await fetch(`${root}cart.js`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
}

/* ------------------------------------------------------- what a grid shows */

/*
 * EVERY FILTER ON A GRID SAYS WHICH CARDS IT REFUSES, and this decides what
 * shows. A card is hidden when ANY filter refuses it: the size chips write
 * data-fz-out-size, the search and filter bar data-fz-out-tools. Each used
 * to set `hidden` itself, and two of them would have undone each other.
 * Then every count for the grid is rewritten and the "nothing here" panel
 * shown or hidden.
 */
export function refreshGrid(grid) {
  if (!grid) return 0;
  let shown = 0;
  for (const item of grid.children) {
    const out = item.dataset.fzOutSize === "1" || item.dataset.fzOutTools === "1";
    item.hidden = out;
    if (!out) shown += 1;
  }
  const filtered = [...grid.children].some((item) => item.hidden);
  grid.toggleAttribute("data-filtered", filtered);
  const id = grid.id;
  for (const count of document.querySelectorAll(`[for="${CSS.escape(id)}"] [data-fz-count]`)) {
    const host = count.closest("[for]");
    const template =
      count.dataset.template || (shown === 1 ? host?.dataset.countOne : host?.dataset.countOther) || "%n";
    let text = template.replace("%n", String(shown));
    // L-88: the size chips' count names the sizes pressed, small to large as
    // the chips stand ("18 clearance items in Kids/Small or Medium").
    const pressed = host ? [...host.querySelectorAll('[data-fz-size][aria-pressed="true"]')] : [];
    if (pressed.length && host.dataset.inSizes) {
      const sizes = joinOr(
        pressed.map((chip) => chip.dataset.fzLabel || chip.dataset.fzSize),
        host.dataset.or || "or",
      );
      text = host.dataset.inSizes.replace("%c", text).replace("%s", sizes);
    }
    count.textContent = text;
  }
  for (const none of document.querySelectorAll(`[data-fz-none][data-for="${CSS.escape(id)}"]`)) {
    none.hidden = shown > 0;
  }
  grid.dispatchEvent(new CustomEvent("fz:grid-filtered", { bubbles: true, detail: { shown } }));
  return shown;
}

/* "A", "A or B", "A, B or C". */
export function joinOr(list, or = "or") {
  if (list.length < 2) return list.join("");
  return `${list.slice(0, -1).join(", ")} ${or} ${list[list.length - 1]}`;
}

/* --------------------------------------------------------------- search */

/*
 * Words, for matching: lower case, accents and the trademark sign gone, any
 * other mark a space. "Gray" and "grey" are one word here, as are "color"
 * and "colour": the store spells one way and a shopper may spell the other.
 */
const SAME = { gray: "grey", color: "colour", colors: "colours", xmas: "christmas" };
export function words(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[™®]/g, "")
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((w) => SAME[w] || w);
}

/* Every word of the query starts some word of the card: "foo blu" finds The
   Foozie in Blue, "beanie" finds the three beanies, "chris" the Christmas. */
// A card's words are split once and kept: the bar asks again on every key
// and for every chip it dims, and re-splitting forty indexes a dozen times a
// keystroke cost up to 47 ms at 4x CPU against a 50 ms budget (9 Oct).
const splitCache = new Map();
function wordsOf(index) {
  let have = splitCache.get(index);
  if (!have) {
    have = words(index);
    splitCache.set(index, have);
  }
  return have;
}

export function matches(index, query) {
  const want = words(query);
  if (want.length === 0) return true;
  const have = wordsOf(index);
  return want.every((w) => have.some((h) => h.startsWith(w)));
}

/* What the bar keeps in the URL, so Back returns to it. Other parameters (a
   preview theme, a UTM) are left exactly as they were. */
export function readState(search) {
  const params = new URLSearchParams(search);
  return {
    q: params.get("q") || "",
    type: params.get("type") || "",
    // ?color= too: the Debut arm reads both, so a link shared from either works.
    colours: (params.get("colour") || params.get("color") || "").split(",").filter(Boolean),
  };
}

export function writeState(href, state) {
  const url = new URL(href);
  const set = (key, value) => (value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
  set("q", state.q.trim());
  set("type", state.type);
  set("colour", state.colours.join(","));
  return url.toString();
}

/* Does one card pass the bar? Type is one value (blank is "All products");
   colours are any-of, and a card matches when ANY of its colours is chosen. */
export function cardPasses(dataset, state) {
  if (state.type && dataset.type !== state.type) return false;
  if (state.colours.length && !state.colours.some((c) => (dataset.colours || "").includes(`|${c}|`))) return false;
  if (!state.q) return true;
  const want = wordsOf(state.q);
  if (want.length === 0) return true;
  const have = wordsOf(dataset.search || "");
  return want.every((w) => have.some((h) => h.startsWith(w)));
}

/* ---------------------------------------------------------------- sort */

/*
 * SORT BY IN PLACE (P1, 25 Oct 2026; Luke on the Debut arm: "every time a
 * different 'sort by' option is chosen ... it forces a page refresh"). The
 * Section Rendering API answers the section in the new order; the cards
 * already on the page are moved to match by data-fz-key, so a size, a
 * quantity or a filter the shopper set stays as it was. Only the newest
 * answer counts. One that cannot be had loads the sorted page instead,
 * which is what the form does without JavaScript.
 */
export function sectionUrl(pathname, sectionId, sortBy) {
  const params = new URLSearchParams();
  params.set("sort_by", sortBy);
  params.set("section_id", sectionId);
  return `${pathname}?${params}`;
}

/* The cards' keys in the order an answer lists them. */
export function keysIn(html) {
  return [...String(html).matchAll(/data-fz-key="([^"]*)"/g)].map((m) => m[1]);
}

/* The keys in the answer's order; one the answer did not name keeps its
   place after them, so a stale or partial answer never loses a card. */
export function orderBy(current, keys) {
  const have = new Set(current);
  const seen = new Set();
  const out = [];
  for (const key of keys) {
    if (have.has(key) && !seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  for (const key of current) if (!seen.has(key)) out.push(key);
  return out;
}

class FzCollectionTools extends HTMLElement {
  #liveTimer = 0;
  #sortToken = 0;
  #state = { q: "", type: "", colours: [] };

  connectedCallback() {
    this.grid = document.getElementById(this.getAttribute("for"));
    this.input = this.querySelector("[data-fz-q]");
    this.live = this.querySelector("[data-fz-live]");
    this.typeChips = [...this.querySelectorAll("[data-fz-type]")];
    this.colourChips = [...this.querySelectorAll("[data-fz-colour]")];
    this.sort = this.querySelector("[data-fz-sort]");
    this.sort?.addEventListener("change", this.#onSort);
    this.sort?.form?.addEventListener("submit", this.#onSortSubmit);
    this.addEventListener("click", this.#onClick);
    this.input?.addEventListener("input", this.#onInput);
    this.input?.addEventListener("keydown", this.#onKey);
    document.addEventListener("click", this.#onClear);

    // A state in the URL (Back, a shared link) is applied once, on arrival.
    // Values this page does not offer are dropped rather than hiding everything.
    const from = readState(window.location.search);
    const types = new Set(this.typeChips.map((c) => c.dataset.fzType));
    const colours = new Set(this.colourChips.map((c) => c.dataset.fzColour));
    this.#state = {
      q: from.q,
      type: types.has(from.type) ? from.type : "",
      colours: from.colours.filter((c) => colours.has(c)),
    };
    if (this.#state.q || this.#state.type || this.#state.colours.length) {
      if (this.input) this.input.value = this.#state.q;
      this.apply({ write: false });
    }
  }

  disconnectedCallback() {
    this.sort?.removeEventListener("change", this.#onSort);
    this.sort?.form?.removeEventListener("submit", this.#onSortSubmit);
    this.removeEventListener("click", this.#onClick);
    this.input?.removeEventListener("input", this.#onInput);
    this.input?.removeEventListener("keydown", this.#onKey);
    document.removeEventListener("click", this.#onClear);
    clearTimeout(this.#liveTimer);
  }

  #onSortSubmit = (event) => {
    event.preventDefault();
    this.#onSort();
  };

  // A keyboard user arrowing through the closed select fires a change per
  // option; each asks for its order, and only the last answer is used.
  #onSort = () => {
    const value = this.sort.value;
    const token = ++this.#sortToken;
    const sorted = new URL(window.location.href);
    sorted.searchParams.set("sort_by", value);
    try {
      history.replaceState(history.state, "", sorted.toString());
    } catch {
      /* a sandboxed frame: the sort still happens */
    }
    const sectionId = (this.closest(".shopify-section")?.id || "").replace(/^shopify-section-/, "");
    const fallback = () => {
      if (token === this.#sortToken) window.location.assign(sorted.toString());
    };
    if (!this.grid || !sectionId || !window.fetch) return fallback();
    this.grid.setAttribute("aria-busy", "true");
    fetch(sectionUrl(window.location.pathname, sectionId, value), { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        if (token !== this.#sortToken) return;
        const keys = keysIn(html);
        if (keys.length === 0) throw new Error("no cards");
        const items = [...this.grid.children];
        const byKey = new Map(items.map((item) => [item.dataset.fzKey, item]));
        const order = orderBy(
          items.map((item) => item.dataset.fzKey),
          keys,
        );
        // appendChild moves a node, so the grid ends in the new order with
        // every card, hidden or not, as it was.
        for (const key of order) {
          const item = byKey.get(key);
          if (item) this.grid.appendChild(item);
        }
        this.grid.removeAttribute("aria-busy");
      })
      .catch(() => {
        if (token !== this.#sortToken) return;
        this.grid.removeAttribute("aria-busy");
        fallback();
      });
  };

  #onInput = () => {
    this.#state.q = this.input.value;
    this.apply();
  };

  #onKey = (event) => {
    if (event.key === "Escape" && this.input.value) {
      event.preventDefault();
      this.input.value = "";
      this.#onInput();
    }
  };

  #onClick = (event) => {
    const chip = event.target.closest("[data-fz-type], [data-fz-colour]");
    if (!chip || !this.contains(chip)) return;
    if ("fzType" in chip.dataset) {
      this.#state.type = chip.dataset.fzType;
    } else {
      const c = chip.dataset.fzColour;
      const on = this.#state.colours.includes(c);
      this.#state.colours = on ? this.#state.colours.filter((x) => x !== c) : [...this.#state.colours, c];
    }
    this.apply();
  };

  #onClear = (event) => {
    const button = event.target.closest("[data-fz-clear]");
    if (!button || button.dataset.fzClear !== this.getAttribute("for")) return;
    this.clear();
    this.input?.focus({ preventScroll: true });
  };

  clear() {
    this.#state = { q: "", type: "", colours: [] };
    if (this.input) this.input.value = "";
    // The size chips are the bar's neighbour on clearance: "clear all" means
    // them too.
    const sizes = document.querySelector(`fz-grid-filter[for="${CSS.escape(this.getAttribute("for"))}"]`);
    if (sizes && typeof sizes.reset === "function") sizes.reset();
    this.apply();
  }

  apply({ write = true } = {}) {
    if (!this.grid) return;
    const state = this.#state;

    // Visible work first, in this task: the chips, then the cards.
    for (const chip of this.typeChips) chip.setAttribute("aria-pressed", String(chip.dataset.fzType === state.type));
    for (const chip of this.colourChips) {
      chip.setAttribute("aria-pressed", String(state.colours.includes(chip.dataset.fzColour)));
    }

    const items = [...this.grid.children];
    for (const item of items) item.dataset.fzOutTools = cardPasses(item.dataset, state) ? "" : "1";
    const shown = refreshGrid(this.grid);

    // A chip that would leave nothing under the OTHER choices is dimmed, not
    // removed: a shopper sees why, and can still press it.
    const without = (patch) => ({ ...state, ...patch });
    for (const chip of this.typeChips) {
      const n = items.filter((i) => cardPasses(i.dataset, without({ type: chip.dataset.fzType }))).length;
      chip.toggleAttribute("data-empty", n === 0);
    }
    for (const chip of this.colourChips) {
      const n = items.filter((i) => cardPasses(i.dataset, without({ colours: [chip.dataset.fzColour] }))).length;
      chip.toggleAttribute("data-empty", n === 0 && !state.colours.includes(chip.dataset.fzColour));
    }

    if (write) {
      try {
        history.replaceState(history.state, "", writeState(window.location.href, state));
      } catch {
        /* a sandboxed frame: the filter still works, Back just forgets it */
      }
    }

    clearTimeout(this.#liveTimer);
    this.#liveTimer = setTimeout(() => {
      if (!this.live) return;
      const template = (shown === 1 ? this.dataset.countOne : this.dataset.countOther) || "%n";
      this.live.textContent = template.replace("%n", String(shown));
    }, 600);
  }
}

/* ------------------------------------------------------------ the filter */

class FzGridFilter extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById(this.getAttribute("for"));
    this.count = this.querySelector("[data-fz-count]");
    this.addEventListener("click", this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#onClick);
  }

  #onClick = (event) => {
    const chip = event.target.closest("[data-fz-size]");
    if (!chip || !this.contains(chip)) return;
    chip.setAttribute("aria-pressed", chip.getAttribute("aria-pressed") === "true" ? "false" : "true");
    this.apply();
  };

  reset() {
    for (const chip of this.querySelectorAll('[data-fz-size][aria-pressed="true"]')) {
      chip.setAttribute("aria-pressed", "false");
    }
    this.apply();
  }

  apply() {
    if (!this.grid) return;
    const chosen = [...this.querySelectorAll('[data-fz-size][aria-pressed="true"]')].map((chip) => chip.dataset.fzSize);
    for (const item of this.grid.children) {
      const sizes = item.dataset.sizes || "";
      const match = chosen.length === 0 || chosen.some((size) => sizes.includes(`|${size}|`));
      item.dataset.fzOutSize = match ? "" : "1";
      // With exactly one size chosen, a colour card takes it as its size
      // unless the shopper has already picked one on that card.
      if (match && chosen.length === 1) {
        const radio = item.querySelector(`input[type="radio"][data-size="${CSS.escape(chosen[0])}"]:not(:disabled)`);
        const picked = item.querySelector('input[type="radio"][data-picked]');
        if (radio && !picked && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }
    refreshGrid(this.grid);
  }
}

/* ------------------------------------------------------- inline add to cart */

class FzGridAdd extends HTMLElement {
  #resetTimer = 0;
  #slowTimer = 0;

  connectedCallback() {
    this.form = this.querySelector("form");
    this.button = this.querySelector("[data-fz-add]");
    this.message = this.querySelector("[data-fz-msg]");
    this.card = this.closest("[data-fz-card]");
    this.line = this.card?.querySelector("[data-fz-line]");
    this.price = this.card?.querySelector("[data-fz-price]");
    this.ship = this.card?.querySelector("[data-fz-ship]");
    this.qty = this.querySelector('input[name="quantity"]');
    this.colourSelect = this.querySelector("select[data-fz-colour-select]");
    if (!this.form) return;
    // Native validation is the no-JS fallback (the size is `required`); with
    // JS the message is ours, next to the controls, not a browser bubble.
    this.form.noValidate = true;
    this.form.addEventListener("submit", this.#onSubmit);
    this.form.addEventListener("change", this.#onChange);
    // A card with a colour select (the PDP's cross-sell): it sits outside the
    // form, so its change is heard here.
    this.colourSelect?.addEventListener("change", this.#onColour);
    // Back may restore a colour other than the one whose sizes the page
    // printed live; the sizes follow the colour, never the other way round.
    const chosen = this.#colour();
    if (chosen && this.#group(chosen)?.disabled) this.#showColour(chosen);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.#onSubmit);
    this.form?.removeEventListener("change", this.#onChange);
    this.colourSelect?.removeEventListener("change", this.#onColour);
    clearTimeout(this.#resetTimer);
    clearTimeout(this.#slowTimer);
  }

  /* The size group that submits: the only one on most cards, the chosen
     colour's (the one not disabled) on a card with a colour select. */
  get sizes() {
    return this.querySelector("[data-fz-sizes]:not([disabled])");
  }

  /* The live size <select>, on a quick card. */
  #sizeSelect() {
    return this.sizes?.querySelector("select[data-fz-size-select]") || null;
  }

  /* The chosen colour's <option>, or null when none is chosen. */
  #colour() {
    const option = this.colourSelect?.selectedOptions[0];
    return option && option.value ? option : null;
  }

  /* What the size control has chosen: the live select's option, or (the
     non-quick colour card) the checked radio. null when nothing is. */
  #chosen() {
    const select = this.#sizeSelect();
    if (select) {
      const option = select.selectedOptions[0];
      return option && option.value ? option : null;
    }
    return this.form.querySelector('input[type="radio"][name="id"]:checked:not(:disabled)');
  }

  #group(colour) {
    return this.querySelector(`[data-fz-group="${CSS.escape(colour.dataset.group || "")}"]`);
  }

  /* A size select back on the size the page preselected for it (its first
     adult size in stock, else any in stock, else a pre-order, adult first,
     else its first: L-114 B, pending; def_list in
     snippets/fz-grid-card). */
  #toPreset(select) {
    const preset = [...select.options].find((o) => o.defaultSelected) || select.options[0];
    if (preset) preset.selected = true;
    return preset || null;
  }

  #onColour = () => {
    const colour = this.#colour();
    if (colour) this.#showColour(colour);
  };

  /*
   * A COLOUR IS CHOSEN. Its size select becomes the only one shown AND the
   * only one enabled (snippets/fz-grid-card says why both, on the fieldset),
   * and every other colour's select goes back to its preselected size, so
   * nothing of it can post. The colour lands on ITS preselected size (its
   * first adult size in stock, then any in stock, then a pre-order, adult
   * first, then its first: L-114 B, pending), exactly as the
   * page prints it when that colour is the one chosen on arrival: the
   * colour's name on the line, that size's price, pre-order line and stock
   * cap, and a disabled "Sold out" only when the colour sells nothing. No
   * size carries over from the colour before, as on Debut (parity, 11 Oct):
   * a carried size can be one this colour has sold out, and Luke asked for
   * the first available. The photo and the links follow, from
   * URLs on the option (no colour's photo loads until it is picked).
   */
  #showColour(colour) {
    const target = colour.dataset.group;
    for (const group of this.querySelectorAll("[data-fz-group]")) {
      const on = group.dataset.fzGroup === target;
      group.hidden = !on;
      group.disabled = !on;
      const select = group.querySelector("select");
      select?.removeAttribute("data-invalid");
      if (!on && select) this.#toPreset(select);
    }
    this.#say("");
    this.colourSelect?.removeAttribute("data-invalid");

    const img = this.card?.querySelector(".fz-grid-card__img:not(.fz-grid-card__img--alt)");
    if (img && colour.dataset.src) {
      if (colour.dataset.srcset) img.srcset = colour.dataset.srcset;
      img.src = colour.dataset.src;
    }
    if (colour.dataset.url) {
      for (const link of this.card?.querySelectorAll(".fz-grid-card__media, .fz-grid-card__title a") || []) {
        link.href = colour.dataset.url;
      }
    }

    const select = this.#sizeSelect();
    const preset = select && this.#toPreset(select);
    if (preset) {
      this.#applySize(preset);
      if (this.line) this.line.textContent = colour.value;
      return;
    }
    this.#soldOut(colour.dataset.soldout === "true");
    if (this.line) this.line.textContent = colour.value;
    if (this.price && colour.dataset.priceHtml) {
      this.price.innerHTML = colour.dataset.priceHtml;
      this.price.classList.toggle("fz-grid-price--sale", colour.dataset.sale === "true");
    }
    if (this.ship) {
      const pre = colour.dataset.preorder === "true";
      if (pre)
        this.ship.textContent = (this.ship.dataset.template || "%date%").replace("%date%", colour.dataset.ship || "");
      this.ship.hidden = !pre;
    }
    this.qty?.removeAttribute("max");
  }

  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches("[data-fz-size-select]")) {
      const option = target.selectedOptions[0];
      if (option && option.value) this.#applySize(option);
      return;
    }
    // A non-quick colour card's size radios (blocks/fz-grid-variants, card_per colour).
    if (!(target instanceof HTMLInputElement) || target.type !== "radio") return;
    if (event.isTrusted) {
      for (const other of this.form.querySelectorAll('input[type="radio"][data-picked]')) {
        other.removeAttribute("data-picked");
      }
      target.setAttribute("data-picked", "");
    }
    this.#applySize(target);
  };

  /* A size is chosen: `choice` is its <option> or its radio, which carry the
     same data-* (snippets/fz-grid-card). */
  #applySize(choice) {
    this.sizes?.removeAttribute("data-invalid");
    this.#sizeSelect()?.removeAttribute("data-invalid");
    this.#say("");
    // A sold-out size looks like any other (Luke, 2026-09-26: "NEVER grey out
    // or strike through size options for out-of-stock SKUs"); the button says
    // so for the size picked, as the PDP's does.
    this.#soldOut(choice.dataset.soldout === "true");
    // The grey line reads "Blue / Medium (US 6-10)" once a size is chosen,
    // exactly as the live clearance card's variant line does.
    if (this.line && choice.dataset.title) this.line.textContent = choice.dataset.title;
    if (this.price && choice.dataset.priceHtml) {
      this.price.innerHTML = choice.dataset.priceHtml;
      this.price.classList.toggle("fz-grid-price--sale", choice.dataset.sale === "true");
    }
    // A size that is the twin's pre-order says so, with ITS date; an in-stock
    // size takes the line away again.
    if (this.ship) {
      const pre = choice.dataset.preorder === "true";
      if (pre)
        this.ship.textContent = (this.ship.dataset.template || "%date%").replace("%date%", choice.dataset.ship || "");
      this.ship.hidden = !pre;
    }
    // The quantity box stops at the stock Shopify would sell, where it is
    // counted; anything else is Shopify's to refuse.
    if (this.qty) {
      if (choice.dataset.max) {
        this.qty.max = choice.dataset.max;
        if (Number(this.qty.value) > Number(choice.dataset.max)) this.qty.value = choice.dataset.max;
      } else {
        this.qty.removeAttribute("max");
      }
    }
  }

  #soldOut(on) {
    const label = this.button?.querySelector(".fz-grid-card__add-label");
    if (!this.button || !label) return;
    if (!this.button.dataset.label) this.button.dataset.label = label.textContent;
    this.button.disabled = on;
    this.button.toggleAttribute("data-sold-out", on);
    label.textContent = on ? this.dataset.soldOut || "Sold out" : this.button.dataset.label;
  }

  #say(text) {
    if (this.message) this.message.textContent = text;
  }

  #onSubmit = (event) => {
    event.preventDefault();
    if (this.button?.dataset.state === "busy") return;

    const data = new FormData(this.form);
    const id = data.get("id");
    // A card with a colour select and no colour chosen (one is chosen on
    // arrival; a restored page can bring none back) asks for the colour first.
    if (this.colourSelect && !this.#colour()) {
      this.colourSelect.setAttribute("data-invalid", "");
      this.#say(this.dataset.pickColour || "Pick a colour first.");
      this.colourSelect.focus({ preventScroll: true });
      return;
    }
    if (this.#chosen()?.dataset.soldout === "true") return;
    if (!id) {
      const select = this.#sizeSelect();
      this.sizes?.setAttribute("data-invalid", "");
      select?.setAttribute("data-invalid", "");
      this.#say(this.dataset.pickSize || "Pick a size first.");
      (select || this.sizes?.querySelector("input:not(:disabled)"))?.focus({ preventScroll: true });
      return;
    }

    const sectionIds = [...document.querySelectorAll("cart-items-component")]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) data.append("sections", sectionIds.join(","));

    // A typed 0, -3 or 2.5 is 1, 0 or 2: never a request Shopify refuses
    // for its shape. Over the stock cap is clamped to it.
    let quantity = Math.floor(Number(data.get("quantity")) || 1);
    if (quantity < 1) quantity = 1;
    const max = Number(this.qty?.max) || 0;
    if (max > 0 && quantity > max) quantity = max;
    if (data.has("quantity")) {
      data.set("quantity", String(quantity));
      if (this.qty) this.qty.value = String(quantity);
    }
    const context = this.dataset.context || "fz-grid";
    const deferred = CartLinesUpdateEvent.createPromise();

    this.#setState("busy");
    const update = new CartLinesUpdateEvent({
      action: "add",
      context,
      lines: [{ merchandiseId: String(id), quantity }],
      promise: deferred.promise,
    });
    // THE LINE AT THE TAP (Luke, 10 Oct: the drawer "opens immediately but it
    // is blank" for 3-5 s). The drawer opens in this dispatch, before Shopify
    // has answered; assets/fz-cart.js paints this card's pair into it from
    // what the card already shows, and the server's line replaces it.
    update.fzOptimistic = this.#optimistic(String(id), quantity);
    this.dispatchEvent(update);

    const routes = (window.Theme && window.Theme.routes) || {};
    fetch(routes.cart_add_url || "/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: data,
    })
      .then((response) => response.json())
      .then(async (response) => {
        const failed = Boolean(response && response.status);
        // ONE ROUND TRIP TO THE LINE. The add already returns the drawer,
        // rendered (the `sections` above), but the event cannot settle until
        // /cart.js has answered too, because the header count and the 4-Pack
        // counter read the cart from it. So the drawer is morphed from the
        // add's own answer now, and Horizon's morph of the same HTML when the
        // event settles finds nothing left to change.
        if (!failed && response.sections) await this.#morphDrawer(response.sections);
        const cart = await fetchCart().catch(() => null);
        deferred.resolve({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
          detail: {
            items: cart ? cart.items : [],
            itemCount: quantity,
            sections: failed ? undefined : response.sections,
            didError: failed,
            source: context,
          },
        });
        if (failed) {
          this.#setState("idle");
          this.#say(response.description || response.message || this.dataset.error || "Something went wrong.");
          return;
        }
        this.#setState("done");
        this.#resetTimer = setTimeout(() => this.#setState("idle"), 1400);
      })
      .catch((error) => {
        deferred.reject(error);
        this.#setState("idle");
        this.#say(this.dataset.error || "Something went wrong. Please try again.");
      });
  };

  /** What the drawer needs to show the pair before Shopify answers: read off this card. */
  #optimistic(variantId, quantity) {
    const card = this.closest("[data-fz-card]");
    // The LIVE size control: on a colour-picker card every other colour's
    // select is disabled through its group, and a disabled select keeps an
    // option checked, so `:enabled` picks the one that posts.
    const chosen = this.form.querySelector('input[name="id"]:checked, select[name="id"]:enabled option:checked');
    const pickedColour = card?.querySelector("select[data-fz-colour-select]")?.selectedOptions[0]?.value || "";
    const title = card?.querySelector(".fz-grid-card__title a");
    const image = /** @type {HTMLImageElement | null} */ (card?.querySelector("img"));
    const price = document.createElement("template");
    price.innerHTML = chosen?.dataset.priceHtml || "";
    for (const hidden of price.content.querySelectorAll(".visually-hidden")) hidden.remove();
    const text = (selector) => price.content.querySelector(selector)?.textContent.trim() || "";
    let name = "";
    for (const node of title?.childNodes || []) {
      if (!(node instanceof Element && node.classList.contains("visually-hidden"))) name += node.textContent;
    }
    return {
      variantId,
      quantity,
      title: name.trim(),
      // The drawer's line names the options as Shopify joins them ("Blue•Small / Kids (US 3-6)"):
      // the card's colour, then the size. A size can hold " / " itself, so the title is never split.
      variant:
        [this.closest("[data-colour]")?.getAttribute("data-colour") || pickedColour, chosen?.dataset.size]
          .filter(Boolean)
          .join("\u2022") ||
        chosen?.dataset.title ||
        "",
      image: image ? image.currentSrc || image.src : "",
      now: quantity === 1 ? text(".fz-grid-price__now") : "",
      compare: quantity === 1 ? text(".fz-grid-price__compare") : "",
    };
  }

  /** The drawer's sections from the add's answer, morphed as Horizon would; a failure leaves it to Horizon. */
  async #morphDrawer(sections) {
    try {
      const { morphSection } = await import("@theme/section-renderer");
      for (const el of document.querySelectorAll("cart-items-component[data-section-id]")) {
        const sectionId = /** @type {HTMLElement} */ (el).dataset.sectionId;
        const html = sectionId && sections[sectionId];
        if (html) await morphSection(sectionId, html, { mode: el.hasAttribute("data-drawer") ? "hydration" : "full" });
      }
    } catch {
      /* Horizon's own morph, when the event settles, still lands it */
    }
  }

  #setState(state) {
    if (!this.button) return;
    clearTimeout(this.#resetTimer);
    clearTimeout(this.#slowTimer);
    this.button.dataset.state = state;
    this.button.removeAttribute("data-slow");
    this.button.setAttribute("aria-busy", state === "busy" ? "true" : "false");
    // A spinner only when the add is slow enough to need one: under 400 ms
    // the label simply holds and the tick arrives.
    if (state === "busy") {
      this.#slowTimer = setTimeout(() => this.button?.setAttribute("data-slow", ""), 400);
    }
    if (state === "done") this.#say("");
  }
}

/* --------------------------------------------------------- bundle counter */

class FzBundleCounter extends HTMLElement {
  #pairs = -1;
  #inflight = null;
  /** An add was made on this page since the last count (L-101). */
  #added = false;

  connectedCallback() {
    this.target = Number(this.dataset.target) || 4;
    this.main = this.querySelector("[data-fz-main]");
    this.sub = this.querySelector("[data-fz-sub]");
    this.inner = this.querySelector(".fz-counter__inner");
    this.pips = [...this.querySelectorAll(".fz-counter__pip")];
    this.#pairs = Number(this.dataset.pairs) || 0;
    this.rule = pairRule(this.dataset);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartEvent);
    document.addEventListener("visibilitychange", this.#onVisible);
    window.addEventListener("pageshow", this.#onPageShow);
    this.querySelector("[data-fz-open-cart]")?.addEventListener("click", this.#openCart);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartEvent);
    document.removeEventListener("visibilitychange", this.#onVisible);
    window.removeEventListener("pageshow", this.#onPageShow);
  }

  #onCartEvent = (event) => {
    // Every add, quantity change and remove, from a grid card or from the
    // drawer, settles its promise once the cart has changed. Wait for that,
    // then count from the cart itself: the event's lines carry no product type.
    if (event.action === "add") this.#added = true;
    Promise.resolve(event.promise)
      .catch(() => null)
      .then(() => this.refresh());
  };

  #onVisible = () => {
    if (!document.hidden) this.refresh();
  };

  #onPageShow = (event) => {
    if (event.persisted) this.refresh();
  };

  #openCart = (event) => {
    if (this.#openDrawer()) event.preventDefault();
  };

  #openDrawer() {
    const drawer = document.getElementById("cart-drawer");
    if (!drawer || typeof drawer.open !== "function") return false;
    if (!drawer.hasAttribute("open")) drawer.open();
    return true;
  }

  refresh() {
    if (this.#inflight) return this.#inflight;
    this.#inflight = fetchCart()
      .then(async (cart) => {
        await learnTags(cart, this.rule);
        this.render(cart);
      })
      .catch(() => {})
      .finally(() => {
        this.#inflight = null;
      });
    return this.#inflight;
  }

  render(cart) {
    const { pairs, cents, discounted } = pairsIn(cart, this.rule);
    const before = this.#pairs;
    this.#pairs = pairs;
    this.dataset.pairs = String(pairs);

    // Four pairs AND the discount applied. Four pairs with no discount (a code
    // that does not combine with it, say) reads "4 of 4 added", never a promise
    // of a free pair the checkout will not give.
    const unlocked = pairs >= this.target && discounted;
    const wasUnlocked = before >= this.target;
    this.toggleAttribute("data-unlocked", unlocked);

    this.pips.forEach((pip, i) => pip.toggleAttribute("data-on", i < Math.min(pairs, this.target)));

    const t = this.dataset;
    this.main.textContent = unlocked
      ? t.textUnlocked
      : (t.textProgress || "%n of 4 added").replace("%n", String(Math.min(pairs, this.target)));

    // At four and over, say what the four cost, read live from the cart after
    // the automatic "Bundle - FREE 4th Pair" discount: "4 pairs for $57.
    // That's $14.25 a pair." (website-and-cro section 7, point 4).
    let sub = "";
    if (unlocked && cents > 0 && cart && cart.currency) {
      sub = (t.textPerPair || "")
        .replace("%n", pairs)
        .replace("%total", money(cents, cart.currency))
        .replace("%each", money(Math.round(cents / pairs), cart.currency));
    }
    this.sub.textContent = sub;

    // THE 4TH PAIR OPENS THE DRAWER (Luke, L-101, 2026-10-08). The page keeps
    // its counter while pairs one to three go in (I-25), and the drawer opens
    // when an add made HERE brings the count to four, so the shopper sees the
    // free pair land. Not on a recount when the tab comes back, not from the
    // drawer's own stepper, and never over a drawer that is already open.
    const added = this.#added;
    this.#added = false;
    if (added && before !== -1 && before < this.target && pairs >= this.target) this.#openDrawer();

    if (before === -1 || before === pairs || reducedMotion()) return;
    this.#pop(pairs > before ? Math.min(pairs, this.target) - 1 : -1, unlocked && !wasUnlocked);
  }

  /*
   * The pip that just filled springs in (280 ms); reaching four makes the
   * whole bar give one brief, springy pop (460 ms, once: a celebration, the
   * one place this page runs past 300 ms). Both are transform-only WAAPI animations,
   * cancelled and replayed if another add lands mid-way, so they never queue.
   */
  #pop(pipIndex, celebrate) {
    const pip = this.pips[pipIndex];
    if (pip) {
      play(pip, [{ transform: "scale(0.6)" }, { transform: "scale(1.25)", offset: 0.45 }, { transform: "scale(1)" }], {
        duration: 280,
        easing: EASE_OUT,
      });
    }
    if (celebrate && this.inner) {
      play(
        this.inner,
        [{ transform: "scale(1)" }, { transform: "scale(1.045)", offset: 0.35 }, { transform: "scale(1)" }],
        { duration: 460, easing: SPRING },
      );
    }
  }
}

/* ------------------------------------------------------ size guide dialog */

document.addEventListener("click", (event) => {
  const opener = event.target.closest("[data-fz-size-guide]");
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute("aria-controls"));
    if (dialog && typeof dialog.showModal === "function") {
      event.preventDefault();
      dialog.showModal();
    }
    return;
  }
  const dialog = event.target.closest("dialog.fz-size-dialog");
  // A click on the backdrop lands on the dialog element itself.
  if (dialog && (event.target === dialog || event.target.closest("[data-fz-close]"))) dialog.close();
});

for (const [name, element] of [
  ["fz-grid-filter", FzGridFilter],
  ["fz-collection-tools", FzCollectionTools],
  ["fz-grid-add", FzGridAdd],
  ["fz-bundle-counter", FzBundleCounter],
]) {
  if (!customElements.get(name)) customElements.define(name, element);
}
